import { memo } from 'react'

import { ContactWorkflowStatus } from 'clickNgo/entities/contact'
import { ContactCommonInfo, ContactGeneralInfo, ContactHPRoles } from 'clickNgo/entities/contact/ui'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { ValidationType } from 'cng-common/features/form/configs'

import { ContactEditorFormKeys } from '../../../configs'
import { useReadOnlyMenuItem } from '../../../hooks'
import {
  TContactEditorContextProps,
  TContactEditorData,
  TContactEditorHandles,
} from '../../../models'
import { ContactEditableContainer } from '../../contactEditableContainer'

import { PartnerHPRoles } from './components/partnerHPRoles'
import { useContactBasicInfoUpdate, useContactBasicInfoValidate, useHPRoleChange } from './hooks'

type TProps = Pick<TContactEditorHandles, 'onContactUpdate'> &
  Pick<TContactEditorData, 'contact' | 'contactFlags'> &
  TContactEditorContextProps

export const WrappedContactBasicInfo = memo<TProps>(
  ({ contact, contactFlags, source, requestPartnerId, isOnboarding, onContactUpdate }) => {
    const { isCurrentMenuItemReadOnly } = useReadOnlyMenuItem()
    const { onApplyHandler, onCancelHandle, hasModifiedValues } = useContactBasicInfoUpdate({
      contact,
      source,
      requestPartnerId,
      onContactUpdate,
    })
    const onSaveAsDraft = isOnboarding
      ? () => onApplyHandler({ workflowStatus: ContactWorkflowStatus.Draft })
      : undefined

    const { generalValidationProps, commonValidationProps } = useContactBasicInfoValidate()

    const { onHPRolesChangeHandler, duplicateHPRoles, hasDuplicateHPRoles } = useHPRoleChange({
      contact,
    })

    return (
      <ContactEditableContainer
        contact={contact}
        contactFlags={contactFlags}
        onApply={onApplyHandler}
        onSaveAsDraft={onSaveAsDraft}
        onCancel={onCancelHandle}
        onUpdate={onContactUpdate}
        hasModifiedValues={hasModifiedValues}
      >
        <ContactGeneralInfo
          isReadOnly={isCurrentMenuItemReadOnly}
          contact={contact}
          formKeys={ContactEditorFormKeys}
          validationType={ValidationType.ONCHANGE}
          {...generalValidationProps}
        />
        <ContactCommonInfo
          isReadOnly={isCurrentMenuItemReadOnly}
          contact={contact}
          formKeys={ContactEditorFormKeys}
          validationType={ValidationType.ONCHANGE}
          isEditMode
          {...commonValidationProps}
        />
        <ContactHPRoles
          isReadOnly={isCurrentMenuItemReadOnly}
          contact={contact}
          formKeys={ContactEditorFormKeys}
          onHPRolesChange={onHPRolesChangeHandler}
          validationType={ValidationType.ONCHANGE}
        />
        <RenderWhen condition={hasDuplicateHPRoles}>
          <PartnerHPRoles duplicateHPRoles={duplicateHPRoles} />
        </RenderWhen>
      </ContactEditableContainer>
    )
  }
)

WrappedContactBasicInfo.displayName = 'WrappedContactBasicInfo'
